import { Skeleton, Stack, useTheme } from "@mui/material";
import type { FC } from "react";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { InspirationColdIntro } from "web_myplann/Inspiration/Components/InspirationColdIntro";
import { InspirationExampleText } from "web_myplann/Inspiration/Components/InspirationExampleText";
import { InspirationStatCardList } from "web_myplann/Inspiration/Components/InspirationStatCardList";
import { useInspirationMoney } from "web_myplann/Inspiration/Hooks/useInspirationMoney";
import type { MortgageRateBand } from "web_myplann/Inspiration/OwnHome/Types/MortgageRateBand";
import { getOwnHomeExample } from "web_myplann/Inspiration/OwnHome/Utils/getOwnHomeExample";
import { INSPIRATION_CURRENCY } from "web_myplann/Inspiration/Utils/inspirationMoney";

const R = Resources.Inspiration.OwnHome;

type Props = {
	rates: MortgageRateBand | null;
};

export const OwnHomeColdIntro: FC<Props> = ({ rates }) => {
	const theme = useTheme();
	const example = rates ? getOwnHomeExample(rates) : null;
	const money = useInspirationMoney();
	const formatAmount = useFormatGoalAmount();
	const propertyOnCard =
		example &&
		formatAmount(example.property, INSPIRATION_CURRENCY, {
			millionsUnit: "letter",
		});

	return (
		<InspirationColdIntro
			intro={R.intro}
			exampleTitle={R.exampleTitle}
			exampleHint={R.exampleHint}
		>
			{example ? (
				<InspirationExampleText
					text={R.exampleBody}
					values={{
						rent: money(example.rent),
						property: money(example.property),
					}}
				/>
			) : (
				<Stack gap={theme.spacingTokens.xs}>
					<Skeleton variant="text" width="100%" height={18} animation="wave" />
					<Skeleton variant="text" width="55%" height={18} animation="wave" />
				</Stack>
			)}

			<InspirationStatCardList
				stats={[
					{
						label: R.rentCardLabel,
						amount: money(0),
						tone: "negative",
					},
					{
						label: R.ownCardLabel,
						amount: propertyOnCard ? (
							`~${propertyOnCard}`
						) : (
							<Skeleton
								variant="text"
								width={72}
								height={20}
								animation="wave"
							/>
						),
						tone: "highlight",
					},
				]}
			/>
		</InspirationColdIntro>
	);
};
